import React, { useState, useEffect, useContext } from "react";
import "./App.css";
import analytics from "./services/analytics";
// import { menuItems, deals } from "./mockData"; // No longer using mock data locally


const CartContext = React.createContext();
const FavoriteContext = React.createContext();
const ToastContext = React.createContext();

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <div className="toast-content">
        <i className={`fas ${type === 'success' ? 'fa-check-circle' : 'fa-info-circle'}`}></i>
        <span>{message}</span>
      </div>
      <button className="toast-close" onClick={onClose}>
        <i className="fas fa-times"></i>
      </button>
    </div>
  );
};

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const FavoriteProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem("favorites");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("favorites", JSON.stringify(favorites));
  }, [favorites]);

  // Helper function to check if two items match (same id and type)
  const itemsMatch = (item1, item2) => {
    return item1.id === item2.id && !!item1.isDeal === !!item2.isDeal;
  };

  const addToFavorites = (item) => {
    setFavorites((prev) => {
      if (prev.find((i) => itemsMatch(i, item))) return prev;
      const newFavorites = [...prev, item];
      // Track analytics
      analytics.addToFavorites({
        ...item,
        category: item.category || (item.isDeal ? 'Deal' : 'General'),
      });
      return newFavorites;
    });
  };

  const removeFromFavorites = (id, isDeal = false) => {
    const itemToRemove = favorites.find(
      (item) => item.id === id && !!item.isDeal === !!isDeal
    );
    
    setFavorites((prev) => 
      prev.filter((item) => !(item.id === id && !!item.isDeal === !!isDeal))
    );
    
    // Track analytics
    if (itemToRemove) {
      analytics.removeFromFavorites({
        ...itemToRemove,
        category: itemToRemove.category || (itemToRemove.isDeal ? 'Deal' : 'General'),
      });
    }
  };

  const isFavorite = (id, isDeal = false) => {
    return favorites.some((item) => item.id === id && !!item.isDeal === !!isDeal);
  };

  const toggleFavorite = (item) => {
    if (isFavorite(item.id, item.isDeal)) {
      removeFromFavorites(item.id, item.isDeal);
    } else {
      addToFavorites(item);
    }
  };

  return (
    <FavoriteContext.Provider
      value={{ favorites, addToFavorites, removeFromFavorites, isFavorite, toggleFavorite }}
    >
      {children}
    </FavoriteContext.Provider>
  );
};

const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  const addToCart = (item) => {
    const existingItem = cartItems.find(
      (cartItem) => cartItem.id === item.id && cartItem.size === item.size
    );

    let updatedCart;
    if (existingItem) {
      updatedCart = cartItems.map((cartItem) =>
        cartItem.id === item.id && cartItem.size === item.size
          ? { ...cartItem, quantity: cartItem.quantity + item.quantity }
          : cartItem
      );
    } else {
      updatedCart = [...cartItems, item];
    }

    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    
    // Track analytics
    analytics.addToCart({
      ...item,
      category: item.category || (item.isDeal ? 'Deal' : 'General'),
    });
  };

  const removeFromCart = (itemId, size) => {
    const itemToRemove = cartItems.find(
      (item) => item.id === itemId && item.size === size
    );
    
    const updatedCart = cartItems.filter(
      (item) => !(item.id === itemId && item.size === size)
    );
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    
    // Track analytics
    if (itemToRemove) {
      analytics.removeFromCart({
        ...itemToRemove,
        category: itemToRemove.category || (itemToRemove.isDeal ? 'Deal' : 'General'),
      });
    }
  };

  const updateQuantity = (itemId, size, newQuantity) => {
    if (newQuantity < 1) return;

    const updatedCart = cartItems.map((item) =>
      item.id === itemId && item.size === size
        ? { ...item, quantity: newQuantity }
        : item
    );
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem("cart");
  };

  const cartTotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

const Modal = ({ isOpen, onClose, children, title }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.keyCode === 27) onClose();
    };

    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY || window.pageYOffset;
      
      // Apply styles to prevent scrolling while preserving visual position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.classList.add("modal-open");
      document.addEventListener("keydown", handleEscape);
      
      // Store scroll position for restoration
      document.body.setAttribute('data-scroll-position', scrollY.toString());
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      
      // Restore scroll position
      const scrollY = document.body.getAttribute('data-scroll-position');
      if (scrollY !== null) {
        // Remove all inline styles
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.classList.remove("modal-open");
        
        // Restore scroll position using requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(scrollY, 10));
        });
        
        document.body.removeAttribute('data-scroll-position');
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button
            className="modal-close"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

const Navbar = ({ openCart, openFavorites }) => {
  const { cartItems } = useContext(CartContext);
  const { favorites } = useContext(FavoriteContext);
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="container">
        <div className="navbar-container">
          <a
            href="#/"
            className="navbar-brand"
            onClick={(e) => {
              e.preventDefault();
              window.location.hash = "#/";
              closeMenu();
            }}
          >
            {/* <i className="fas fa-pizza-slice"></i> Sangi <span>Pizza Steak World</span> */}
            <img
              src="https://sangipizza.com/logo.ico"
              alt="Sangi Pizza Steak World Logo"
              loading="lazy"
            />
            Sangi <span>Pizza Steak World</span>
          </a>

          <button
            className={`hamburger ${isMenuOpen ? "active" : ""}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className={`nav-menu ${isMenuOpen ? "open" : ""}`}>
            <a href="#our-menu" className="nav-link" onClick={closeMenu}>
              Menu
            </a>
            <a href="#deals" className="nav-link" onClick={closeMenu}>
              Deals
            </a>
            <a href="#about" className="nav-link" onClick={closeMenu}>
              About Us
            </a>
            <a href="#contact" className="nav-link" onClick={closeMenu}>
              Contact Us
            </a>
            <a
              href="#"
              className="favorite-icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openFavorites();
                closeMenu();
              }}
              aria-label="View favorites"
            >
              <i className="far fa-heart"></i>
              {favorites.length > 0 && (
                <span className="cart-count">{favorites.length}</span>
              )}
            </a>
            <a
              href="#"
              className="cart-icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openCart();
                closeMenu();
              }}
              aria-label="View cart"
            >
              <i className="fas fa-shopping-cart"></i>
              {cartItems.length > 0 && (
                <span className="cart-count">{cartItems.length}</span>
              )}
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

const HeroSection = () => {
  return (
    <section className="hero">
      <div className="container">
        <div className="hero-content">
          <h1 className="hero-title">Artisanal Pizza & Premium Delivery</h1>
          <p className="hero-subtitle">
            Experience culinary excellence with our handcrafted pizzas and
            gourmet meals.
          </p>
          <a href="#our-menu" className="hero-cta">
            Order Now <i className="fas fa-arrow-right"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const { cartItems } = useContext(CartContext);
  const [menuItems, setMenuItems] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [menuRes, dealsRes] = await Promise.all([
          fetch("https://sangipizza.com/menu.json", { cache: "no-store" }),
          fetch("https://sangipizza.com/deals.json", { cache: "no-store" }),
        ]);

        if (!menuRes.ok || !dealsRes.ok) {
          throw new Error("Failed to fetch menu or deals data.");
        }

        const menuData = await menuRes.json();
        const dealsData = await dealsRes.json();

        setMenuItems(menuData);
        setDeals(dealsData || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Unable to load menu. Please check your internet connection.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const categories = [...new Set(menuItems.map((item) => item.category))];
  const [activeCategory, setActiveCategory] = useState("Pizza");

  useEffect(() => {
    if (categories.length > 0 && !categories.includes(activeCategory)) {
      setActiveCategory(categories[0]);
    } else if (categories.length > 0 && activeCategory === "Pizza") {
      // Keep Pizza if it exists, otherwise first category
      if (!categories.includes("Pizza")) setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);


  useEffect(() => {
    const toggleGoToTopBtn = () => {
      const btn = document.getElementById("goToTopBtn");
      if (btn) {
        if (window.scrollY > 300) {
          btn.classList.add("show");
        } else {
          btn.classList.remove("show");
        }
      }
    };

    window.addEventListener("scroll", toggleGoToTopBtn);
    toggleGoToTopBtn();
    return () => window.removeEventListener("scroll", toggleGoToTopBtn);
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading deliciousness...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <i className="fas fa-wifi-slash"></i>
        <h2>Oops! Connection Issue</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="btn btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (

    <>
      {/* <HeroSection /> */}
      <div className="page">
        <div className="container">
          <div id="deals">
            <h2 className="section-title">Special Deals</h2>
            <div className="menu-grid">
              {deals.map((deal) => (
                <DealCard key={deal.id} deal={deal} />
              ))}
            </div>
          </div>

          <div id="our-menu">
            <h2 className="section-title">Our Menu</h2>
            <div className="category-tabs-container">
              <div className="category-tabs">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setActiveCategory(category);
                      analytics.filterByCategory(category);
                    }}
                    className={`category-tab ${activeCategory === category ? "active" : ""
                      }`}
                    aria-label={`Filter by ${category}`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
            <div className="menu-grid">
              {menuItems
                .filter((item) => item.category === activeCategory)
                .map((item) => (
                  <MenuItemCard key={item.id} item={item} />
                ))}
            </div>
          </div>

          <section id="about" className="about-section">
            <div className="container">
              <h2 className="section-title">About Us</h2>
              <div className="about-content">
                <p className="lead">
                  At Sangi Pizza Steak World, we craft an unparalleled dining
                  experience with every bite. Our menu harmonizes the artistry
                  of gourmet pizzas with the opulence of tender steaks, using
                  the freshest ingredients and our signature recipes. Nestled in
                  the heart of Okara, we blend warm hospitality with a passion
                  for perfection. Savor the elegance of our dishes, crafted with
                  love and dedication for every guest.
                </p>
                <p className="lead">
                  Our chefs combine traditional techniques with innovative
                  twists, ensuring each dish is a masterpiece. Whether you're
                  craving a classic Margherita or a sizzling steak, we bring the
                  finest quality to Okara and beyond.
                </p>
              </div>
            </div>
          </section>

          <section id="contact" className="contact-section">
            <div className="container">
              <h2 className="section-title">Contact Us</h2>
              <div className="contact-grid">
                <div className="contact-info">
                  <h4>Visit Us</h4>
                  <p>
                    <a
                      href="https://www.google.com/maps/place/SANGI+Pizza+and+steak+world/@30.8022245,73.4312007,17z"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View Sangi Pizza Steak World on Google Maps"
                    >
                      MA Jinnah Road Near Jinnah Park, Okara 56300, Pakistan
                    </a>
                  </p>
                  <h4>Phone</h4>
                  <p>
                    <a
                      href="tel:+923321808000"
                      rel="nofollow"
                      aria-label="Call Sangi Pizza Steak World"
                    >
                      +92 332 1808000
                    </a>
                  </p>
                  <h4>Email</h4>
                  <p>
                    <a
                      href="mailto:sangipizza00@gmail.com"
                      rel="nofollow"
                      aria-label="Email Sangi Pizza Steak World"
                    >
                      sangipizza00@gmail.com
                    </a>
                  </p>
                  <h4>Operating Hours</h4>
                  <p>11:00 AM - 02:00 AM</p>
                </div>
                <div className="map-section">
                  <h4>Our Location</h4>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3418.258406087256!2d73.4312007!3d30.8022245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3922a7005c2fabd7%3A0xb5c794f496595b3a!2sSANGI+Pizza+and+steak+world!5e0!3m2!1sen!2sus!4v1698771234567!5m2!1sen!2sus"
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Sangi Pizza Steak World Location in Okara"
                  ></iframe>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
      <button
        id="goToTopBtn"
        className="go-to-top-btn"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Go to top"
      >
        <i className="fas fa-arrow-up"></i>
      </button>
    </>
  );
};

const MenuItemCard = ({ item }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [isAdded, setIsAdded] = useState(false); // New state for button feedback
  const { addToCart } = useContext(CartContext);
  const { toggleFavorite, isFavorite } = useContext(FavoriteContext);
  const { showToast } = useContext(ToastContext);

  const handleAddToCart = () => {
    let price = item.price;
    let size = null;

    if (item.sizes && item.sizes.length > 0) {
      price = item.sizes.find((s) => s.label === selectedSize)?.price || price;
      size = selectedSize;
    } else if (item.variants && item.variants.length > 0) {
      price =
        item.variants.find((v) => v.option === selectedVariant)?.price || price;
    }

    addToCart({
      id: item.id,
      name: item.name,
      price,
      quantity,
      size,
      variant: selectedVariant,
      image: item.image,
    });

    setQuantity(1);
    setSelectedSize(null);
    setSelectedVariant(null);

    // Use state for feedback instead of DOM manipulation
    setIsAdded(true);
    showToast(`${item.name} added to cart!`, "success");
    setTimeout(() => {
      setIsAdded(false);
    }, 1500);
  };

  useEffect(() => {
    if (item.sizes && item.sizes.length > 0 && !selectedSize) {
      setSelectedSize(item.sizes[0].label);
    }
    if (item.variants && item.variants.length > 0 && !selectedVariant) {
      setSelectedVariant(item.variants[0].option);
    }
  }, [item, selectedSize, selectedVariant]);

  return (
    <div className="card menu-item-card">
      <div className="item-image">
        <img src={item.image} alt={item.name} loading="lazy" />
        <span className="item-category">{item.category}</span>
        <button
          className={`favorite-btn ${isFavorite(item.id, false) ? "active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite({
              id: item.id,
              name: item.name,
              image: item.image,
              category: item.category,
              price: item.price || (item.sizes && item.sizes[0]?.price) || (item.variants && item.variants[0]?.price),
              description: item.description,
              sizes: item.sizes,
              variants: item.variants,
              isDeal: false
            });
          }}
          aria-label={isFavorite(item.id, false) ? "Remove from favorites" : "Add to favorites"}
        >
          <i className={`${isFavorite(item.id, false) ? "fas" : "far"} fa-heart`}></i>
        </button>
      </div>
      <div className="item-details">
        <h3 className="item-name">{item.name}</h3>
        {item.description && (
          <p className="item-description">{item.description}</p>
        )}

        {item.sizes && item.sizes.length > 0 && (
          <div className="form-group">
            <label>Size:</label>
            <div className="size-selector">
              {item.sizes.map((size) => (
                <button
                  key={size.label}
                  onClick={() => setSelectedSize(size.label)}
                  className={selectedSize === size.label ? "active" : ""}
                  aria-label={`Select ${size.label} size`}
                  type="button"
                >
                  {size.label} - Rs. {size.price}
                </button>
              ))}
            </div>
          </div>
        )}

        {item.variants && item.variants.length > 0 && (
          <div className="form-group">
            <label>Options:</label>
            <div className="variant-selector">
              {item.variants.map((variant) => (
                <button
                  key={variant.option}
                  onClick={() => setSelectedVariant(variant.option)}
                  className={selectedVariant === variant.option ? "active" : ""}
                  aria-label={`Select ${variant.option} variant`}
                  type="button"
                >
                  {variant.option} - Rs. {variant.price}
                </button>
              ))}
            </div>
          </div>
        )}

        {!item.sizes && !item.variants && (
          <div className="item-price">Rs. {item.price}</div>
        )}

        <div className="item-actions">
          <div className="quantity-selector">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              aria-label="Decrease quantity"
              type="button"
            >
              -
            </button>
            <span>{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              aria-label="Increase quantity"
              type="button"
            >
              +
            </button>
          </div>
          <button
            className="add-to-cart-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddToCart();
            }}
            disabled={
              (item.sizes && !selectedSize) ||
              (item.variants && !selectedVariant)
            }
            aria-label={`Add ${item.name} to cart`}
            type="button"
          >
            {isAdded ? (
              <>
                <i className="fas fa-check"></i> Added
              </>
            ) : (
              <>
                <i className="fas fa-shopping-cart"></i> Add to Cart
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const DealCard = ({ deal }) => {
  const { addToCart } = useContext(CartContext);
  const { toggleFavorite, isFavorite } = useContext(FavoriteContext);
  const { showToast } = useContext(ToastContext);
  const [isAdded, setIsAdded] = useState(false); // New state for button feedback

  const handleAddToCart = () => {
    addToCart({
      id: deal.id,
      name: deal.title,
      price: deal.price,
      quantity: 1,
      isDeal: true,
      image: deal.image,
    });

    showToast(`${deal.title} added to cart!`, "success");

    // Use state for feedback instead of DOM manipulation
    setIsAdded(true);
    setTimeout(() => {
      setIsAdded(false);
    }, 1500);
  };

  return (
    <div className="card menu-item-card">
      <div className="item-image">
        <img src={deal.image} alt={deal.title} loading="lazy" />
        <span className="item-category">Deal</span>
        <button
          className={`favorite-btn ${isFavorite(deal.id, true) ? "active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite({
              id: deal.id,
              name: deal.title,
              image: deal.image,
              price: deal.price,
              isDeal: true,
              description: deal.description,
              items: deal.items
            });
          }}
          aria-label={isFavorite(deal.id, true) ? "Remove from favorites" : "Add to favorites"}
        >
          <i className={`${isFavorite(deal.id, true) ? "fas" : "far"} fa-heart`}></i>
        </button>
      </div>
      <div className="item-details">
        <h3 className="item-name">{deal.title}</h3>
        <p className="item-description">{deal.description}</p>

        <div className="deal-items">
          <h4>Includes:</h4>
          <ul>
            {deal.items.map((item, index) => (
              <li key={index}>
                {item.quantity}x {item.name} {item.size ? `(${item.size})` : ""}
              </li>
            ))}
          </ul>
        </div>

        <div className="item-price">Rs. {deal.price}</div>

        <div className="item-actions">
          <button
            className="add-to-cart-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddToCart();
            }}
            aria-label={`Add ${deal.title} to cart`}
            type="button"
          >
            {isAdded ? (
              <>
                <i className="fas fa-check"></i> Added
              </>
            ) : (
              <>
                <i className="fas fa-shopping-cart"></i> Add to Cart
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-brand-section">
            <div className="footer-brand">Sangi Pizza Steak World</div>
            <p className="footer-description">
              Crafting premium pizzas and gourmet flavors with passion.
            </p>
            <div className="social-links">
              <a
                href="https://web.facebook.com/people/SANGI-Pizza-Steak-World/61574400519863"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
              {/* <a href="#" aria-label="Instagram">
                <i className="fab fa-instagram"></i>
              </a>
              <a href="#" aria-label="Twitter">
                <i className="fab fa-twitter"></i>
              </a>
              <a href="#" aria-label="WhatsApp">
                <i className="fab fa-whatsapp"></i>
              </a> */}
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            &copy; 2025 Sangi Pizza Steak World. Crafted with{" "}
            <i className="fas fa-heart"></i> by{" "}
            <a
              href="https://web.facebook.com/protechnologiespvt"
              target="_blank"
              rel="noopener noreferrer"
            >
              PRO Technologies
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

const CartContent = ({ onCheckout, onClose }) => {
  const { cartItems, removeFromCart, updateQuantity, clearCart, cartTotal } =
    useContext(CartContext);

  if (cartItems.length === 0) {
    return (
      <div className="empty-state">
        <i className="fas fa-shopping-cart"></i>
        <p>Your cart is empty</p>
        <a href="#our-menu" className="btn btn-primary" onClick={onClose}>
          Browse Menu
        </a>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <div className="cart-items">
        {cartItems.map((item, index) => (
          <div key={index} className="cart-item">
            <div className="cart-item-image">
              <img src={item.image} alt={item.name} loading="lazy" />
            </div>
            <div className="cart-item-details">
              <div className="cart-item-header">
                <div className="cart-item-name">
                  {item.name} {item.size && `(${item.size})`}
                </div>
                <div className="cart-item-price">
                  Rs. {item.price * item.quantity}
                </div>
              </div>
              <div className="cart-item-actions">
                <div className="quantity-selector">
                  <button
                    onClick={() =>
                      updateQuantity(item.id, item.size, item.quantity - 1)
                    }
                    aria-label="Decrease quantity"
                    type="button"
                  >
                    -
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    onClick={() =>
                      updateQuantity(item.id, item.size, item.quantity + 1)
                    }
                    aria-label="Increase quantity"
                    type="button"
                  >
                    +
                  </button>
                </div>
                <button
                  className="remove-item-btn"
                  onClick={() => removeFromCart(item.id, item.size)}
                  aria-label={`Remove ${item.name} from cart`}
                  type="button"
                >
                  <i className="fas fa-trash"></i> Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="cart-summary">
        <h3>Order Summary</h3>
        <div className="summary-row">
          <span>Subtotal:</span>
          <span>Rs. {cartTotal}</span>
        </div>
        <div className="summary-row">
          <span>Delivery Fee:</span>
          <span>Rs. 0.0</span>
        </div>
        <div className="summary-row total">
          <span>Total:</span>
          <span>Rs. {cartTotal + 0}</span>
        </div>

        <button
          className="checkout-btn"
          onClick={onCheckout}
          aria-label="Proceed to checkout"
          type="button"
        >
          Proceed to Checkout
        </button>

        <button
          className="clear-cart-btn"
          onClick={clearCart}
          aria-label="Clear cart"
          type="button"
        >
          Clear Cart
        </button>
      </div>
    </div>
  );
};

const CheckoutContent = ({ onClose }) => {
  const { cartItems, cartTotal, clearCart } = useContext(CartContext);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    instructions: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let message = `New Order:\n\nItems:\n${cartItems
      .map(
        (item) =>
          `${item.quantity}x ${item.name} ${item.size ? `(${item.size})` : ""}`
      )
      .join("\n")}\n\nName: ${formData.name}\nPhone: ${formData.phone
      }\nAddress: ${formData.address}\nInstructions: ${formData.instructions || "None"
      }`;
    const encodedMessage = encodeURIComponent(message);
    
    // Track purchase event
    const transactionId = `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    analytics.purchase(transactionId, cartItems, cartTotal, 0);
    
    clearCart();
    onClose();
    window.location.href = `https://wa.me/03321808000?text=${encodedMessage}`;
  };

  return (
    <div className="cart-container">
      <form onSubmit={handleSubmit} className="checkout-form">
        <h3>Delivery Information</h3>
        <div className="form-group">
          <label htmlFor="name">Full Name</label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter your full name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            id="phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder="Enter your phone number"
          />
        </div>

        <div className="form-group">
          <label htmlFor="address">Delivery Address</label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            rows="3"
            placeholder="Enter your complete delivery address"
          />
        </div>

        <div className="form-group">
          <label htmlFor="instructions">Special Instructions (Optional)</label>
          <textarea
            id="instructions"
            name="instructions"
            value={formData.instructions}
            onChange={handleChange}
            rows="2"
            placeholder="Any special instructions for delivery"
          />
        </div>

        <div className="form-group">
          <h3>Payment Method</h3>
          <div className="payment-option">
            <input
              type="radio"
              id="cod"
              name="payment"
              value="cod"
              checked
              readOnly
            />
            <label htmlFor="cod">Cash on Delivery</label>
          </div>
        </div>

        <button
          type="submit"
          className="form-submit"
          aria-label="Confirm order"
        >
          Confirm Order
        </button>
      </form>

      <div className="cart-summary">
        <h3>Your Order</h3>
        {cartItems.map((item, index) => (
          <div key={index} className="order-item">
            <span>
              {item.quantity}x {item.name}
              {item.size && ` (${item.size})`}
            </span>
            <span>Rs. {item.price * item.quantity}</span>
          </div>
        ))}

        <div className="summary-row">
          <span>Subtotal:</span>
          <span>Rs. {cartTotal}</span>
        </div>
        <div className="summary-row">
          <span>Delivery Fee:</span>
          <span>Rs. 0.0</span>
        </div>
        <div className="summary-row total">
          <span>Total:</span>
          <span>Rs. {cartTotal + 0}</span>
        </div>
      </div>
    </div>
  );
};

const FavoritesContent = ({ onClose }) => {
  const { favorites, removeFromFavorites } = useContext(FavoriteContext);
  const { addToCart } = useContext(CartContext);
  const { showToast } = useContext(ToastContext);

  if (favorites.length === 0) {
    return (
      <div className="empty-state">
        <i className="far fa-heart"></i>
        <p>No favorites yet</p>
        <button className="btn btn-primary" onClick={onClose}>
          Explore Menu
        </button>
      </div>
    );
  }

  const handleAddToCart = (e, item) => {
    e.stopPropagation();
    addToCart({
      ...item,
      quantity: 1,
    });
    showToast(`${item.name} added to cart!`, "success");
  };

  const handleRemoveFromFavorites = (e, item) => {
    e.stopPropagation();
    removeFromFavorites(item.id, item.isDeal);
  };

  return (
    <div className="cart-container">
      <div className="cart-items">
        {favorites.map((item) => (
          <div key={item.isDeal ? `deal-${item.id}` : `menu-${item.id}`} className="cart-item">
            <div className="cart-item-image">
              <img src={item.image} alt={item.name} loading="lazy" />
            </div>
            <div className="cart-item-details">
              <div className="cart-item-header">
                <div className="cart-item-name">{item.name}</div>
                <div className="cart-item-price">Rs. {item.price}</div>
              </div>
              <div className="cart-item-actions">
                <button
                  className="add-to-cart-btn"
                  onClick={(e) => handleAddToCart(e, item)}
                  aria-label={`Add ${item.name} to cart`}
                  type="button"
                >
                  <i className="fas fa-shopping-cart"></i> Add to Cart
                </button>
                <button
                  className="remove-item-btn"
                  onClick={(e) => handleRemoveFromFavorites(e, item)}
                  aria-label={`Remove ${item.name} from favorites`}
                  type="button"
                >
                  <i className="fas fa-trash"></i> Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const App = () => {
  const [showCartModal, setShowCartModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const { cartItems } = useContext(CartContext);

  const openCart = () => {
    setShowCartModal(true);
    analytics.openModal('cart');
  };

  const closeCart = () => {
    setShowCartModal(false);
    analytics.closeModal('cart');
  };

  const openFavorites = () => {
    setShowFavoritesModal(true);
    analytics.openModal('favorites');
  };

  const closeFavorites = () => {
    setShowFavoritesModal(false);
    analytics.closeModal('favorites');
  };

  const openCheckout = () => {
    setShowCartModal(false);
    setShowCheckoutModal(true);
    analytics.openModal('checkout');
    // Track begin checkout
    const cartTotal = cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
    analytics.beginCheckout(cartItems, cartTotal);
  };

  const closeCheckout = () => {
    setShowCheckoutModal(false);
    analytics.closeModal('checkout');
  };

  return (
    <div className="app">
      <Navbar openCart={openCart} openFavorites={openFavorites} />
      <HomePage />

      {cartItems.length > 0 && (
        <button
          id="floatingCartBtn"
          className="floating-cart-btn"
          onClick={openCart}
          aria-label="View cart"
        >
          <i className="fas fa-shopping-cart"></i>
          <span className="cart-count">{cartItems.length}</span>
        </button>
      )}

      <Modal isOpen={showCartModal} onClose={closeCart} title="Your Cart">
        <CartContent onCheckout={openCheckout} onClose={closeCart} />
      </Modal>

      <Modal
        isOpen={showFavoritesModal}
        onClose={closeFavorites}
        title="My Favorites"
      >
        <FavoritesContent onClose={closeFavorites} />
      </Modal>

      <Modal
        isOpen={showCheckoutModal}
        onClose={closeCheckout}
        title="Checkout"
      >
        <CheckoutContent onClose={closeCheckout} />
      </Modal>
    </div>
  );
};

export default function AppWrapper() {
  return (
    <ToastProvider>
      <FavoriteProvider>
        <CartProvider>
          <App />
        </CartProvider>
      </FavoriteProvider>
    </ToastProvider>
  );
}